import { MovableDialog } from '@/components/shared/MovableDialog';
import { type CSSProperties, type ReactElement } from 'react';
import type { SpecFileDiffResult } from '@/api/v2Client';

// Phase spec-file-upload-diff (TODO-071) — two-step upload gate.
//
// Shown after the publisher selects a DTD/XSD file but BEFORE we persist
// it as the new active spec. The publisher sees added / removed / unchanged
// section counts plus the explicit list of sections that will be added or
// removed, and can either Cancel (close, do nothing) or Confirm Upload
// (call the parent's onConfirm, which performs the persist+activate step).

export interface SpecFileDiffModalProps {
  readonly fileName: string;
  readonly result: SpecFileDiffResult;
  readonly busy?: boolean;
  readonly errorMessage?: string | null;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function SpecFileDiffModal({
  fileName,
  result,
  busy,
  errorMessage,
  onCancel,
  onConfirm,
}: SpecFileDiffModalProps): ReactElement {
  // T15-D1 — the server says when there is nothing to compare against
  // (lists null + reason); that is stated, never shown as "0 removed".
  const notComparable = result.comparable === false;
  const addedCount = result.newSections?.length ?? 0;
  const removedCount = result.removedSections?.length ?? 0;
  const hasRemovals = removedCount > 0;

  // phase-movable-dialogs — shared movable shell, resizable (diff tables).
  return (
    <MovableDialog
      persistKey="specFiles.diff"
      title="Preview spec file diff"
      ariaLabel="Spec file diff preview"
      testId="spec-file-diff-modal"
      onClose={busy ? undefined : onCancel}
      escDisabled={busy}
      closeOnBackdrop={!busy}
      resizable
      minSize={{ width: 560, height: 400 }}
      width="min(640px, 92vw)"
      maxHeight="88vh"
      zIndex={320}
    >
      <>
        <header style={{ marginBottom: 16 }}>
          <p style={{ margin: '4px 0 0 0', color: 'var(--text-muted)', fontSize: 13 }}>
            <code data-testid="spec-file-diff-filename">{fileName}</code>
            {result.currentVersion && (
              <>
                {' — replacing '}
                <code data-testid="spec-file-diff-current-version">{result.currentVersion}</code>
              </>
            )}
            {result.newVersion && (
              <>
                {' with '}
                <code data-testid="spec-file-diff-new-version">{result.newVersion}</code>
              </>
            )}
          </p>
        </header>

        {notComparable && (
          <p data-testid="spec-file-diff-not-comparable" style={{ fontSize: 13, marginBottom: 16 }}>
            Sections not compared: {result.reason} (The file declares {result.sectionsInFile} section
            {result.sectionsInFile === 1 ? '' : 's'}.)
          </p>
        )}
        {!notComparable && (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, marginBottom: 16 }}>
          <li
            data-testid="spec-file-diff-unchanged-count"
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14 }}
          >
            <span aria-hidden style={{ color: 'var(--text-muted)' }}>✅</span>
            <span>
              <strong>{result.unchangedCount}</strong> section
              {result.unchangedCount === 1 ? '' : 's'} unchanged
            </span>
          </li>
          <li
            data-testid="spec-file-diff-added-count"
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 14 }}
          >
            <span aria-hidden style={{ color: 'var(--green)' }}>➕</span>
            <span>
              <strong>{addedCount}</strong> new section{addedCount === 1 ? '' : 's'}
            </span>
          </li>
          <li
            data-testid="spec-file-diff-removed-count"
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}
          >
            <span aria-hidden style={{ color: 'var(--red)' }}>❌</span>
            <span>
              <strong>{removedCount}</strong> removed section{removedCount === 1 ? '' : 's'}
            </span>
          </li>
        </ul>
        )}

        {addedCount > 0 && (
          <section style={{ marginBottom: 12 }} data-testid="spec-file-diff-added-section">
            <h4 style={sectionTitleStyle}>New sections</h4>
            <ul style={sectionListStyle}>
              {(result.newSections ?? []).map((s) => (
                <li
                  key={`added-${s}`}
                  data-testid={`spec-file-diff-added-${s}`}
                  style={{ ...sectionItemStyle, color: 'var(--green)' }}
                >
                  + {s}
                </li>
              ))}
            </ul>
          </section>
        )}

        {hasRemovals && (
          <section style={{ marginBottom: 12 }} data-testid="spec-file-diff-removed-section">
            <h4 style={sectionTitleStyle}>Removed sections</h4>
            <ul
              style={{
                ...sectionListStyle,
                background: 'var(--red-bg, rgba(220,38,38,0.06))',
                borderRadius: 6,
                padding: 8,
              }}
            >
              {(result.removedSections ?? []).map((s) => (
                <li
                  key={`removed-${s}`}
                  data-testid={`spec-file-diff-removed-${s}`}
                  style={{ ...sectionItemStyle, color: 'var(--red)' }}
                >
                  − {s}
                </li>
              ))}
            </ul>
            <p
              data-testid="spec-file-diff-removal-warning"
              style={{ marginTop: 8, fontSize: 12, color: 'var(--red)' }}
            >
              Submissions referencing these sections may fail validation after the
              new spec is activated. Confirm only if the removals are intentional.
            </p>
          </section>
        )}

        {errorMessage && (
          <div
            role="alert"
            data-testid="spec-file-diff-error"
            style={{
              color: 'var(--red)',
              background: 'var(--red-bg, rgba(220,38,38,0.06))',
              borderRadius: 4,
              padding: '8px 12px',
              marginBottom: 12,
              fontSize: 13,
            }}
          >
            {errorMessage}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            data-testid="spec-file-diff-cancel"
            onClick={onCancel}
            disabled={busy}
            style={cancelBtnStyle(!!busy)}
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="spec-file-diff-confirm"
            onClick={onConfirm}
            disabled={busy}
            style={confirmBtnStyle(!!busy, hasRemovals)}
          >
            {busy ? 'Uploading…' : 'Confirm Upload'}
          </button>
        </div>
      </>
    </MovableDialog>
  );
}

const sectionTitleStyle: CSSProperties = {
  margin: '0 0 6px 0',
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
};

const sectionListStyle: CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  maxHeight: 160,
  overflowY: 'auto',
  fontSize: 13,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
};

const sectionItemStyle: CSSProperties = {
  padding: '2px 6px',
};

function cancelBtnStyle(busy: boolean): CSSProperties {
  return {
    background: 'none',
    color: 'var(--text-muted)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    padding: '8px 14px',
    fontSize: 13,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };
}

function confirmBtnStyle(busy: boolean, hasRemovals: boolean): CSSProperties {
  return {
    background: hasRemovals ? 'var(--red, #dc2626)' : 'var(--accent)',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: busy ? 'wait' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };
}
